import type { OpportunityRelatedExtraGuestSurcharge } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { convertValueByFX } from '~/utils/convertValueByFX';
import { floatify } from '~/utils/mathUtils';

import { parseDealOptionNumber } from '../dealOptions/dealOptionsFormatters';

/** Salesforce holds the sell in AUD only, so every other currency is reached by converting from it. */
export const EGS_SELL_SOURCE_CURRENCY = 'AUD';

// Neither is exact on both sides: `contracted` converts the sell, `aud` takes the cost from Salesforce's
// `converted_cost__c`, which has no decimals.
export type EgsWriteCurrencyMode = 'aud' | 'contracted';

export const DEFAULT_EGS_WRITE_CURRENCY_MODE: EgsWriteCurrencyMode = 'contracted';

/**
 * Undefined, not a fallback: the caller pairs this with which cost field to read, so defaulting to AUD here
 * would invite writing a vendor-currency amount under the wrong code.
 */
export function getEgsContractedCurrency(rows: Array<OpportunityRelatedExtraGuestSurcharge>): string | undefined {
  return rows.map((row) => (row.cost_price_currency ?? '').trim()).find(Boolean);
}

// `cost_price__c` is a vendor-currency figure, so it is only safe once that currency is known. Reading it
// while the record declares AUD writes a contracted amount under the wrong code.
export function getSlotCostAmount({
  contractedCurrency,
  row,
}: {
  contractedCurrency: string | undefined;
  row: OpportunityRelatedExtraGuestSurcharge;
}) {
  return parseDealOptionNumber(contractedCurrency ? row.cost_price__c : row.converted_cost__c);
}

export function getSlotSellAmount({
  rateLookup,
  row,
  writeCurrency,
}: {
  rateLookup: Record<string, number>;
  row: OpportunityRelatedExtraGuestSurcharge;
  writeCurrency: string;
}) {
  return convertEgsSellAmount({
    amount: parseDealOptionNumber(row.sell_price_aud__c) ?? 0,
    rateLookup,
    toCurrency: writeCurrency,
  });
}

// Undefined rather than the throw `convertValueByFX` raises, so `missing_fx_rate` can refuse that room rate
// instead of taking the wizard down.
export function convertEgsSellAmount({
  amount,
  rateLookup,
  toCurrency,
}: {
  amount: number;
  rateLookup: Record<string, number>;
  toCurrency: string;
}): number | undefined {
  try {
    const converted = convertValueByFX({
      fromCurrency: EGS_SELL_SOURCE_CURRENCY,
      fxRates: rateLookup,
      toCurrency,
      value: amount,
    });

    return floatify(converted);
  } catch {
    return undefined;
  }
}
